import { fetchNotes } from '@/api/basecamp.api';
import type {
    FetchConstructionPlanCostComparisonParams,
    ICatalogueItem,
    IConstructionPlanWeeklyComparisonRequest,
    IConstructionPlansReq,
    IContractor,
    IFindCatalogueItemsRelationParams,
    IInspectionQuestion,
    IRenovationInspection,
    NoteExpandOpts,
    UpsertCatalogueItemParams,
} from '@condo/domain';
import { type QueryObserverOptions, useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import { isNil, omitBy, uniq } from 'lodash-es';
import { type MaybeRef, type Ref, computed, isRef, unref } from 'vue';

import {
    type IAttachCatalogueItemSubItemsParams,
    type IDetachCatalogueItemSubItemParams,
    addCatalogueItemsToTemplate,
    attachCatalogueItemSubItems,
    createCatalogueItem,
    createConstructionTemplate,
    deleteCatalogueItemFromConstructionTemplate,
    detachCatalogueItemSubItem,
    fetchConstructionPlanCostComparison,
    fetchConstructionTemplate,
    fetchConstructionTemplates,
    fetchInspectionQuestions,
    fetchRenovationInspections,
    fetchWeeklyCostComparisonOfConstructionPlan,
    getCatalogue,
    getCatalogueItem,
    getConstructionPlans,
    getContractors,
    getRenovationCostTrackingForEstate,
    saveCatalogueItem,
    updateCatalogueItemTemplateRelation,
} from '@/api/renovation.api';
import { withLoading } from '@/shared/helpers';

export const useConstructionTemplate = (
    constructionTemplateId: number,
    opts?: { withCatalogueItemRelations?: boolean; withCatalogueItemAvailabilities?: boolean },
) => {
    const queryClient = useQueryClient();

    const query = useQuery(['construction-templates', constructionTemplateId, opts], async () => fetchConstructionTemplate(constructionTemplateId, opts), {
        initialData: null,
    });

    const { mutateAsync: deleteCatalogueItemMutation } = useMutation(
        async (catalogueItemId: number) => deleteCatalogueItemFromConstructionTemplate(constructionTemplateId, catalogueItemId),
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['catalogue-items']);
                queryClient.invalidateQueries(['construction-templates', constructionTemplateId]);
            },
        },
    );

    const { mutateAsync: addCatalogueItemsMutation } = useMutation(
        async (catalogueItems: { catalogueItemId: number; ignoredRoomAvailabilities: Record<string, boolean> }[]) =>
            addCatalogueItemsToTemplate(constructionTemplateId, catalogueItems),
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['catalogue-items']);
                queryClient.invalidateQueries(['construction-templates', constructionTemplateId]);
            },
        },
    );

    return { ...query, deleteCatalogueItem: deleteCatalogueItemMutation, addCatalogueItems: addCatalogueItemsMutation };
};

export const useConstructionTemplates = (findParams: { name?: string[]; opts?: { withCatalogueItems?: boolean } } = {}) => {
    const query = useQuery(['construction-templates', findParams], async () => fetchConstructionTemplates(findParams.name, findParams.opts), {
        initialData: [],
        refetchOnMount: true,
    });

    const { mutateAsync: createTemplateMutation } = useMutation(async (createParams: { name: string; opts?: { copyFromTemplateId?: number } }) =>
        createConstructionTemplate(createParams.name, createParams?.opts),
    );

    return { ...query, createTemplate: createTemplateMutation };
};

export const useCatalogueItems = paramsOrRef => {
    const searchParams = computed(() => (isRef(paramsOrRef) ? paramsOrRef.value : paramsOrRef));

    const query = useQuery(['catalogue-items', searchParams], async () => getCatalogue(omitBy(searchParams.value, val => !val)), {
        initialData: { catalogueItems: [], count: 0 },
    });

    const { mutateAsync: createCatalogueItemMutation } = useMutation(async (catalogueItem: UpsertCatalogueItemParams) => createCatalogueItem(catalogueItem), {
        onSuccess: () => {
            query.refetch();
        },
    });

    return { ...query, createCatalogueItem: createCatalogueItemMutation };
};

export const useCatalogueItem = (catalogueItemIdOrRef: number | Ref<number>, opts: IFindCatalogueItemsRelationParams = {}) => {
    const catalogueItemId = computed(() => unref(catalogueItemIdOrRef));

    const queryClient = useQueryClient();

    const query = useQuery(['catalogue-items', catalogueItemId, opts], async () => withLoading(() => getCatalogueItem(catalogueItemId.value, opts)), {
        initialData: null,
    });

    const { mutateAsync: saveCatalogueItemMut } = useMutation(
        async (catalogueItem: Partial<ICatalogueItem>) => saveCatalogueItem({ catalogueItemId: catalogueItemId.value, ...catalogueItem }),
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['catalogue-items', catalogueItemId]);
            },
        },
    );

    const { mutateAsync: attachSubItemsMut } = useMutation(
        async (params: IAttachCatalogueItemSubItemsParams) => attachCatalogueItemSubItems(catalogueItemId.value, params),
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['catalogue-items', catalogueItemId]);
            },
        },
    );

    const { mutateAsync: detachSubItemMut } = useMutation(
        async (params: IDetachCatalogueItemSubItemParams) => detachCatalogueItemSubItem(catalogueItemId.value, params),
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['catalogue-items', catalogueItemId]);
            },
        },
    );

    const { mutateAsync: updateTemplateRelationMut } = useMutation(
        async (params: { constructionTemplateId: number; catalogueItemId: number; ignoredRoomAvailabilities: Record<string, boolean> }) =>
            updateCatalogueItemTemplateRelation(params.constructionTemplateId, params.catalogueItemId, {
                ignoredRoomAvailabilities: params.ignoredRoomAvailabilities,
            }),
        {
            onSuccess: (_, variables) => {
                queryClient.invalidateQueries(['catalogue-items', variables.catalogueItemId]);
            },
        },
    );

    const { mutateAsync: createTemplateRelationMut } = useMutation(
        async (constructionTemplateId: number) =>
            addCatalogueItemsToTemplate(constructionTemplateId, [{ catalogueItemId: catalogueItemId.value, ignoredRoomAvailabilities: {} }]),
        {
            onSuccess: (_, constructionTemplateId) => {
                queryClient.invalidateQueries(['catalogue-items', catalogueItemId.value]);
                queryClient.invalidateQueries(['construction-templates', constructionTemplateId]);
            },
        },
    );

    const { mutateAsync: deleteTemplateRelationMut } = useMutation(
        async (constructionTemplateId: number) => deleteCatalogueItemFromConstructionTemplate(constructionTemplateId, catalogueItemId.value),
        {
            onSuccess: constructionTemplateId => {
                queryClient.invalidateQueries(['catalogue-items', catalogueItemId.value]);
                queryClient.invalidateQueries(['construction-templates', constructionTemplateId]);
            },
        },
    );

    return {
        ...query,
        save: saveCatalogueItemMut,
        attachSubItems: attachSubItemsMut,
        detachSubItem: detachSubItemMut,
        updateTemplateRelation: updateTemplateRelationMut,
        createTemplateRelation: createTemplateRelationMut,
        deleteTemplateRelation: deleteTemplateRelationMut,
    };
};

export const useCatalogueItemSubItems = (catalogueItem: ICatalogueItem, opts: IFindCatalogueItemsRelationParams = {}) => {
    const requiredSubItemIds = computed(() => catalogueItem.availabilities.flatMap(avail => avail.requiredSubItemIds));
    const optionalSubItemIds = computed(() => catalogueItem.availabilities.flatMap(avail => avail.optionalSubItemIds));

    const { data, ...rest } = useCatalogueItems({
        catalogueItemIds: uniq([...requiredSubItemIds.value, ...optionalSubItemIds.value]),
        ...opts,
    });

    const catalogueItemWithSubItems = computed(() => {
        return {
            ...catalogueItem,
            requiredSubItems: data.value.catalogueItems.filter(it => requiredSubItemIds.value.includes(it.catalogueItemId)),
            optionalSubItems: data.value.catalogueItems.filter(it => optionalSubItemIds.value.includes(it.catalogueItemId)),
        };
    });

    return { ...rest, data: catalogueItemWithSubItems };
};

export const useInspections = (estateId: number) => {
    return useQuery<IRenovationInspection[]>(['inspections', estateId], async () => fetchRenovationInspections(estateId, { withRelations: true }), {
        initialData: null,
        enabled: !!estateId,
    });
};

export const useRenovationProcessNotes = (processId: number, renovationInspectionsIds: number[], expandOptsOrRef?: MaybeRef<NoteExpandOpts>) => {
    const expandOpts = computed(() => unref(expandOptsOrRef));
    const isEnabled = computed(() => Number.isFinite(processId) && processId > 0);

    return useQuery(
        ['notes', processId, renovationInspectionsIds, expandOpts],
        () => {
            const promises = [fetchNotes({ renovationProcessId: processId }, expandOpts.value)];
            if (renovationInspectionsIds?.length) {
                promises.push(fetchNotes({ renovationInspectionsIds }, expandOpts.value));
            }
            return Promise.all(promises);
        },
        {
            initialData: null,
            enabled: isEnabled,
        },
    );
};

export const useRenovationInspectionQuestions = (paramsOrRef?: MaybeRef<Partial<IInspectionQuestion>>) => {
    const params = computed(() => omitBy(unref(paramsOrRef), isNil));
    return useQuery(['renovation-inspection-questions', params], () => fetchInspectionQuestions(params.value));
};

export const useConstructionPlanCostComparison = (paramsOrRef?: MaybeRef<FetchConstructionPlanCostComparisonParams>) => {
    const params = computed(() => unref(paramsOrRef));
    const isEnabled = computed(() => !!params.value.constructionPlanId && !!params.value.startDate && !!params.value.endDate && !!params.value.regionName);

    return useQuery(['construction-plan-cost-comparison', params], () => fetchConstructionPlanCostComparison(params.value), {
        enabled: isEnabled,
        initialData: [],
    });
};

export const useEstateRenovationCostTracking = (estateIdOrRef: MaybeRef<number>) => {
    const estateId = computed(() => unref(estateIdOrRef));
    const isEnabled = computed(() => !!estateId.value);

    return useQuery(['estate-renovation-cost-tracking', estateId], () => getRenovationCostTrackingForEstate(estateId.value), {
        enabled: isEnabled,
        initialData: null,
    });
};

export const useConstructionPlans = (paramsOrRef?: MaybeRef<Partial<IConstructionPlansReq['Querystring']>>) => {
    const params = computed(() => unref(paramsOrRef));

    return useQuery(['construction-plans', paramsOrRef], () => getConstructionPlans(params.value), {
        initialData: [],
    });
};

export const useConstructionPlanCostWeeklyComparison = (paramsOrRef?: MaybeRef<IConstructionPlanWeeklyComparisonRequest>) => {
    const params = computed(() => unref(paramsOrRef));
    const isEnabled = computed(() => !!params.value.constructionPlanId && !!params.value.startDate && !!params.value.endDate && !!params.value.regionName);
    return useQuery(['construction-plan-weekly-cost-comparison', params], () => fetchWeeklyCostComparisonOfConstructionPlan(params.value), {
        enabled: isEnabled,
        initialData: [],
    });
};

export const useContractors = (
    reactiveOpts?: MaybeRef<Pick<QueryObserverOptions<IContractor[]>, 'enabled'>>,
    passThroughOpts?: Pick<QueryObserverOptions<IContractor[]>, 'onSuccess'>,
) => {
    const opts = computed(() => unref(reactiveOpts));
    const isEnabled = computed(() => isNil(opts.value?.enabled) || (!isNil(opts.value?.enabled) && opts.value.enabled));
    return useQuery(['renovation/contractors'], () => getContractors(), {
        initialData: [],
        enabled: isEnabled,
        ...passThroughOpts,
    });
};
